import { createSlice } from '@reduxjs/toolkit';
import { remove } from 'lodash';
import { UiState as UiStateEnum } from 'constants/ui';
import { stateName } from './constants';
export const initialFormState = {
  errors: {
    paymentError: null,
    isKycConfirmationNeeded: false,
    hasChecksumError: false,
    hasShippingDiscountError: false,
    isPaymentFailedAfterRedirect: false,
    isPaymentProcessingTakesTooLong: false
  }
};
export const initialErrorUiState = {
  byName: {},
  names: []
};
export const initialUiState = {
  errors: initialErrorUiState,
  checkoutData: UiStateEnum.Idle
};
export const initialState = {
  ui: initialUiState,
  formState: initialFormState,
  payment: null,
  isPaymentProcessing: false,
  checkoutData: null,
  configuration: null,
  blikPaymentAuth: null,
  paymentAuthAction: null,
  twoFactorVerification: null
};

const setFieldError = (draft, action) => {
  const {
    fieldError
  } = action.payload;
  draft.ui.errors.byName[fieldError.field] = fieldError.value;
  if (draft.ui.errors.names.includes(fieldError.field)) return;
  draft.ui.errors.names.push(fieldError.field);
};

const setFieldErrors = (draft, action) => {
  const {
    fieldErrors
  } = action.payload;
  fieldErrors.forEach(error => {
    draft.ui.errors.byName[error.field] = error.value;
  });
  draft.ui.errors.names = fieldErrors.map(error => error.field);
};

const removeFieldError = (draft, action) => {
  const {
    fieldName
  } = action.payload;

  if (draft.ui.errors.byName[fieldName]) {
    delete draft.ui.errors.byName[fieldName];
  }

  remove(draft.ui.errors.names, name => name === fieldName);
};

const removeFieldsErrors = draft => {
  draft.ui.errors.byName = {};
  draft.ui.errors.names = [];
};

const getCheckoutDataRequest = draft => {
  draft.ui.checkoutData = UiStateEnum.Pending;
};

const getCheckoutDataSuccess = (draft, action) => {
  draft.checkoutData = action.payload.checkout;
  draft.ui.checkoutData = UiStateEnum.Success;
};

const getCheckoutDataFailure = draft => {
  draft.isPaymentProcessing = false;
  draft.ui.checkoutData = UiStateEnum.Failure;
};

const updateCheckoutDataRequest = draft => {
  draft.ui.checkoutData = UiStateEnum.Pending;
};

const updateCheckoutDataSuccess = (draft, action) => {
  draft.checkoutData = action.payload.checkout;
  draft.ui.checkoutData = UiStateEnum.Success;
};

const updateCheckoutDataFailure = draft => {
  draft.isPaymentProcessing = false;
  draft.ui.checkoutData = UiStateEnum.Failure;
};

const setTwoFactorVerification = (draft, action) => {
  draft.twoFactorVerification = action.payload;
};

const clearTwoFactorVerification = draft => {
  draft.twoFactorVerification = null;
};

const setIsPaymentProcessing = (draft, action) => {
  draft.isPaymentProcessing = action.payload.isPaymentProcessing;
};

const setPayment = (draft, action) => {
  draft.payment = action.payload;
};

const setPaymentError = (draft, action) => {
  draft.formState.errors.paymentError = action.payload.error;
};

const clearPaymentError = draft => {
  draft.formState.errors.paymentError = null;
};

const setPaymentAuthAction = (draft, action) => {
  draft.paymentAuthAction = action.payload.paymentAuthAction;
};

const clearPaymentAuthAction = draft => {
  draft.paymentAuthAction = null;
};

const setPaymentAuthActionError = (draft, action) => {
  const {
    message
  } = action.payload;
  draft.formState.errors.paymentError = {
    message
  };
  draft.paymentAuthAction = null;
  draft.isPaymentProcessing = false;
};

const cancelPaymentAuth = draft => {
  draft.paymentAuthAction = null;
  draft.isPaymentProcessing = false;
};

const setIsKycConfirmationNeeded = (draft, action) => {
  const {
    isNeeded
  } = action.payload;
  draft.formState.errors.isKycConfirmationNeeded = isNeeded;
};

const fetchConfigurationSuccess = (draft, action) => {
  draft.configuration = action.payload.configuration;
};

const fetchConfigurationFailure = draft => {
  draft.configuration = null;
};

const setHasChecksumError = (draft, action) => {
  const {
    hasChecksumError
  } = action.payload;
  draft.formState.errors.hasChecksumError = hasChecksumError;
};

const setHasShippingDiscountError = (draft, action) => {
  const {
    hasShippingDiscountError
  } = action.payload;
  draft.formState.errors.hasShippingDiscountError = hasShippingDiscountError;
};

const setIsPaymentProcessingTakesTooLong = (draft, action) => {
  const {
    isPaymentProcessingTakesTooLong
  } = action.payload;
  draft.formState.errors.isPaymentProcessingTakesTooLong = isPaymentProcessingTakesTooLong;
};

const setIsPaymentFailedAfterRedirect = (draft, action) => {
  const {
    isPaymentFailedAfterRedirect
  } = action.payload;
  draft.formState.errors.isPaymentFailedAfterRedirect = isPaymentFailedAfterRedirect;
};

const clearIsPaymentFailedAfterRedirect = draft => {
  draft.formState.errors.isPaymentFailedAfterRedirect = false;
};

const confirmBlikAuth = (draft, action) => {
  const {
    blikCode
  } = action.payload;
  draft.blikPaymentAuth = {
    blikCode
  };
};

const clearBlikAuth = draft => {
  draft.blikPaymentAuth = null;
};

const checkoutSlice = createSlice({
  name: stateName,
  initialState,
  reducers: {
    setFieldError,
    setFieldErrors,
    removeFieldError,
    removeFieldsErrors,
    getCheckoutDataRequest,
    getCheckoutDataSuccess,
    getCheckoutDataFailure,
    updateCheckoutDataRequest,
    updateCheckoutDataSuccess,
    updateCheckoutDataFailure,
    setTwoFactorVerification,
    clearTwoFactorVerification,
    setIsPaymentProcessing,
    setPayment,
    setPaymentError,
    clearPaymentError,
    setHasChecksumError,
    setHasShippingDiscountError,
    setIsKycConfirmationNeeded,
    setIsPaymentFailedAfterRedirect,
    clearIsPaymentFailedAfterRedirect,
    setIsPaymentProcessingTakesTooLong,
    clearBlikAuth,
    confirmBlikAuth,
    cancelPaymentAuth,
    setPaymentAuthAction,
    clearPaymentAuthAction,
    setPaymentAuthActionError,
    fetchConfigurationSuccess,
    fetchConfigurationFailure
  }
});
export const {
  actions
} = checkoutSlice;
export const plug = {
  [stateName]: checkoutSlice.reducer
};
export default checkoutSlice.reducer;